import React from 'react';
import { Layout, Typography } from 'antd';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AppFooter from '../components/Footer';
import './Terms.scss';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const Terms: React.FC = () => {
  return (
    <Layout className="terms-layout">
      <Navbar />
      <Content className="terms-content">
        <section className="terms-section">
          <Title level={1} className="terms-title">Terms of Service</Title>
          <Paragraph className="terms-date">Last Modified: May 5, 2025</Paragraph>

          <Title level={2} className="terms-subtitle">1. Company Information</Title>
          <Paragraph>
            GMeow Labs, Ltd. ("Replicats", "we", "our", or "us") owns and operates Replicats. We are incorporated in the
            British Virgin Islands, and comply with applicable British Virgin Islands laws and regulations. Replicats
            provides a software platform that allows users to build, deploy, and interact with AI-powered Agents. “Agents”
            are user-configured autonomous programs that execute investment strategies based on the user’s selected
            parameters. These agents can autonomously manage digital asset portfolios based on user preferences and market
            data analysis. Replicats does not control or advise on agent behavior; users are solely responsible for agent
            setup, performance, and outcomes. Replicats is a technology provider and is not a broker-dealer, investment
            advisor, financial planner, or registered investment advisor under any jurisdiction. We do not provide
            investment advice, recommendations, or personalized financial guidance.
          </Paragraph>

          <Title level={2} className="terms-subtitle">2. Agreement to Terms</Title>
          <Paragraph>
            By accessing or using our services, you agree to be bound by these Terms of Service ("Terms") and our{' '}
            <Link to="/privacy-policy" className="policy-link">
              Privacy Policy
            </Link>
            . If you do not agree to these Terms, do not use the services.
          </Paragraph>

          <Title level={2} className="terms-subtitle">3. Services Provided</Title>
          <Paragraph>
            We provide a cloud-based software as a service non-custodial, on-chain trading platform enabling users to
            interact directly with decentralized financial protocols through AI-powered investment agents. Our services are
            purely technological in nature, providing automated software tools without human intervention or personalized
            advice. Our services are software-based and offered "as is."
          </Paragraph>

          <Title level={2} className="terms-subtitle">4. Eligibility</Title>
          <Paragraph>
            You must be at least 18 years old and capable of forming a legally binding contract under applicable law. By
            using the Services, you represent and warrant that:
            <ul>
              <li>You meet these eligibility requirements;</li>
              <li>
                You are not located in a jurisdiction subject to U.S. sanctions including but not limited to Iran, North
                Korea, Syria, Cuba, Russia, Belarus, and the Crimea, Donetsk, and Luhansk regions of Ukraine, and other
                jurisdictions as may be designated from time to time, as administered under the International Emergency
                Economic Powers Act (IEEPA), the Trading with the Enemy Act, and sanctions programs maintained by the
                Office of Foreign Assets Control (OFAC);
              </li>
              <li>Your use complies with all applicable laws and regulations.</li>
              <li>
                You agree that you may be required to provide accurate identity verification information as required by
                applicable anti-money laundering regulations. The Company reserves the right to request additional
                documentation and to restrict or suspend access pending verification.
              </li>
            </ul>
          </Paragraph>

          <Title level={2} className="terms-subtitle">5. Use Restrictions</Title>
          <Paragraph>
            You agree not to:
            <ul>
              <li>Use the services for any illegal, fraudulent, or unauthorized purposes.</li>
              <li>Interfere with or disrupt the integrity or performance of our services.</li>
              <li>Circumvent any security features or access controls of the platform.</li>
              <li>Use automated systems to access or interact with the platform without permission.</li>
              <li>Upload or transmit viruses or malicious code.</li>
              <li>Attempt to reverse engineer, decompile, or extract source code from the services.</li>
            </ul>
            We reserve the right to investigate and take appropriate action, including suspending or terminating access,
            against any use of the services that violates these restrictions.
          </Paragraph>

          <Title level={2} className="terms-subtitle">6. Subscription and Payment</Title>
          <Paragraph>
            We offer subscription-based access to our platform with various tiers. Subscription pricing is published on our
            website and is subject to change. Subscriptions are billed monthly or yearly and charged per agent. Payment may
            be made using cryptocurrency, credit card, or tokens. All fees are non-refundable unless required by law.
            Subscriptions may be cancelled at any time, with cancellation taking effect at the end of the current billing
            cycle.
          </Paragraph>

          <Title level={2} className="terms-subtitle">7. AI Investment Agents</Title>
          <Paragraph>
            Our platform enables the creation and management of AI-powered investment agents. These agents operate based on
            algorithmic models and machine learning technologies, functioning according to configurations, settings, and
            operational parameters that remain under the user's control and direction throughout their use. You understand
            that:
            <ul>
              <li>Agent performance is not guaranteed</li>
              <li>Past performance does not guarantee future results</li>
              <li>Models may have inherent limitations and biases</li>
              <li>Agents operate autonomously based on your specified parameters</li>
              <li>
                The agents' decisions are automated and do not constitute investment advice, recommendations, or
                personalized guidance
              </li>
              <li>You are solely responsible for all investment decisions and outcomes</li>
            </ul>
          </Paragraph>

          <Title level={2} className="terms-subtitle">8. Blockchain Specifics</Title>
          <Paragraph>
            Our platform currently operates on Base chain with plans to expand to other blockchains. You acknowledge:
            <ul>
              <li>Network congestion may affect transaction execution</li>
              <li>Smart contract interactions carry inherent risks</li>
              <li>Blockchain transactions are irreversible once confirmed</li>
            </ul>
          </Paragraph>

          <Title level={2} className="terms-subtitle">9. Risk Disclosures</Title>
          <Paragraph>
            <Text strong>WARNING: DIGITAL ASSET INVESTMENTS CARRY EXTREME RISK</Text>
            <br />
            Investment activities carry substantial risk of loss. By using our services, you acknowledge:
            <ul>
              <li>Digital assets are highly volatile and you may lose all invested funds</li>
              <li>Algorithmic trading strategies may underperform or fail completely</li>
              <li>Technical failures can occur at any time</li>
              <li>AI models have limitations and may not respond optimally to all market conditions</li>
              <li>You should not invest funds you cannot afford to lose entirely without affecting your financial security or lifestyle.</li>
              <li>The platform is in beta/experimental stage and may contain bugs, errors, or vulnerabilities</li>
              <li>Smart contracts may have undiscovered flaws that could result in total loss of funds</li>
              <li>AI models may make errors, produce unexpected results, or behave unpredictably</li>
              <li>Past testing or performance provides no guarantee of future operation</li>
            </ul>
          </Paragraph>

          <Title level={2} className="terms-subtitle">10. Regulatory Compliance</Title>
          <Paragraph>
            Our services may be subject to various regulations. We aim to comply with applicable British Virgin Islands
            laws, including securities regulations as they may apply to our services. We are not registered as an
            investment advisor, broker-dealer, or financial institution in any jurisdiction. Our AI agents provide
            automated trading execution based on algorithms, not regulated investment advice. You are responsible for
            determining whether your use of our services complies with laws in your jurisdiction.
          </Paragraph>

          <Title level={2} className="terms-subtitle">11. Indemnification</Title>
          <Paragraph>
            You agree to indemnify, defend, and hold harmless the company and its affiliates, directors, officers,
            employees, and agents from any and all claims, demands, actions, damages, losses (including, without
            limitation, any trading or market losses, loss of principal, diminution in value of digital assets, or lost
            profits), liabilities, costs, or expenses (including legal fees) arising out of or related to your use of the
            services, your breach of these Terms, or your violation of any applicable laws or rights of third parties. For
            avoidance of doubt, the indemnity above extends to any claim that seeks to hold the Company liable for trading
            or market losses you incur through the autonomous actions of any Agent.
          </Paragraph>

          <Title level={2} className="terms-subtitle">12. Intellectual Property and Feedback</Title>
          <Paragraph>
            All rights, title, and interest in and to the services, including all related intellectual property, are owned
            by us or our licensors. If you provide suggestions, comments, or other feedback about the services, you grant
            us a non-exclusive, royalty-free, perpetual, irrevocable, and sublicensable license to use and incorporate such
            feedback without restriction or obligation to you.
          </Paragraph>

          <Title level={2} className="terms-subtitle">13. Data Classification</Title>
          <Paragraph>
            We do not collect or store any personally identifiable information unless explicitly provided by you for
            account-related communications. Transactional data processed through the services remains on-chain and is
            subject to the privacy and transparency characteristics of the relevant blockchain. We may collect aggregated
            and anonymized usage data for improving service performance, which will not identify you.
          </Paragraph>

          <Title level={2} className="terms-subtitle">14. Non-Custodial Nature</Title>
          <Paragraph>
            Our platform is non-custodial. We do not have direct access to your digital assets and facilitate third-party
            custody solutions for our users. When you create an account with us, we facilitate the creation of custody
            solutions for you using third-party services. You retain full control of your digital assets and your private
            keys at all times. We do not hold, custody, control, or manage your funds or private keys at any time. Our
            platform connects to and relies on third-party non-custodial wallet solutions and protocols. You are solely
            responsible for the security and management of your wallet credentials, private keys, and digital assets. We
            have no ability to recover, restore, or return your digital assets if lost or compromised.
          </Paragraph>

          <Title level={2} className="terms-subtitle">15. Termination for Convenience</Title>
          <Paragraph>
            We may suspend or terminate your access to the services at any time, with or without cause or notice. You may
            stop using the services at any time. Upon termination, your right to use the services will immediately cease,
            but certain provisions of these Terms will survive, including ownership, disclaimers, indemnity, and
            limitations of liability.
          </Paragraph>

          <Title level={2} className="terms-subtitle">16. Modifications to Terms</Title>
          <Paragraph>
            We may update these Terms from time to time. Changes will be posted on this page with a revised effective date.
            Continued use of the services after the changes become effective constitutes your acceptance of the updated
            Terms.
          </Paragraph>

          <Title level={2} className="terms-subtitle">17. Disclaimer of Warranties and Limitation of Liability</Title>
          <Paragraph>
            The services are provided "as is" and "as available," without warranties of any kind, whether express, implied,
            or statutory. We do not guarantee uninterrupted or error-free operation or that the services will meet your
            requirements. To the fullest extent permitted by law, we disclaim all warranties, including implied warranties
            of merchantability, fitness for a particular purpose, and non-infringement.
          </Paragraph>
          <Paragraph>
            In no event will we or our affiliates be liable for any indirect, incidental, special, consequential, or
            punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of
            data, use, goodwill, or trading or market losses (including loss of principal, slippage, liquidation, or
            unrealised gains), resulting from your access to or use of the services, or from any interruption, suspension,
            or termination of the services (whether or not such interruption was within our control).
          </Paragraph>

          <Title level={2} className="terms-subtitle">18. Export Compliance</Title>
          <Paragraph>
            You agree to comply with all applicable export control laws and regulations. You represent that you are not
            located in or ordinarily resident in any country subject to U.S. or other applicable embargoes or export
            restrictions. You may not use the services if you are subject to such restrictions.
          </Paragraph>

          <Title level={2} className="terms-subtitle">19. Governing Law</Title>
          <Paragraph>
            These Terms are governed by the laws of British Virgin Islands, without regard to conflict of law principles.
          </Paragraph>

          <Title level={2} className="terms-subtitle">20. Dispute Resolution</Title>
          <Paragraph>
            Any dispute, controversy, or claim arising out of or relating to these Terms or the services shall be settled
            by binding arbitration in accordance with the UNCITRAL Arbitration Rules. The place of arbitration shall be
            Road Town, British Virgin Islands. The language to be used in the arbitral proceedings shall be English. The
            arbitral award shall be final and binding. Notwithstanding the foregoing, either party may seek preliminary
            injunctive relief or other equitable remedies in any court of competent jurisdiction to prevent irreparable
            harm pending the outcome of arbitration.
          </Paragraph>

          <Title level={2} className="terms-subtitle">21. Third-Party Disputes</Title>
          <Paragraph>
            We are not responsible for interactions between you and third parties facilitated through or connected to our
            services, including but not limited to protocol providers, wallet providers, or liquidity platforms. Any
            disputes arising from such interactions are solely between you and the third party. We do not intervene in or
            mediate such disputes.
          </Paragraph>

          <Paragraph className="terms-contact">
            <Text strong>
              If you have questions, contact us at{' '}
              <a href="mailto:support@replicats.ai">support@replicats.ai</a>.
            </Text>
          </Paragraph>
        </section>
      </Content>
      <AppFooter />
    </Layout>
  );
};

export default Terms;